import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import { useAuth } from '../../context/AuthContext';
import {
  COMMISSION_STATUS_LABELS,
  COMMISSION_STATUS_TONES,
  REFERRAL_PIPELINE,
  REFERRAL_STATUSES,
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUS_TONES,
  ReferralStatus,
} from '../../constants/partnerNetwork';
import {
  ActivityEntry,
  AdminReferral,
  ReferralCommission,
  addReferralNote,
  getReferral,
  markCommissionPaid,
  setReferralCommission,
  updateReferralStatus,
} from '../../services/partnerNetwork';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatRate,
  toDateInput,
} from '../../utils/partnerFormat';
import {
  Alert,
  Modal,
  Pipeline,
  Spinner,
  StatusBadge,
  inputClass,
} from './ui';

const ReferralDetail: React.FC = () => {
  const { id = '' } = useParams();
  const { admin } = useAuth();

  const [referral, setReferral] = useState<AdminReferral | null>(null);
  const [commission, setCommission] = useState<ReferralCommission | null>(null);
  const [activities, setActivities] = useState<ActivityEntry[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusDraft, setStatusDraft] = useState<ReferralStatus>('new');
  const [statusNote, setStatusNote] = useState('');

  const [commissionOpen, setCommissionOpen] = useState(false);
  const [valueDraft, setValueDraft] = useState('');
  const [rateDraft, setRateDraft] = useState('');
  const [markDue, setMarkDue] = useState(false);

  const [paidOpen, setPaidOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [paidDate, setPaidDate] = useState(toDateInput(new Date()));

  const [noteDraft, setNoteDraft] = useState('');

  const canManage = admin?.role === 'admin' || admin?.role === 'superadmin';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getReferral(id);
      setReferral(response.referral);
      setCommission(response.commission);
      setActivities(response.activities);
      setStatusDraft(response.referral.status);
      setValueDraft(
        response.referral.projectValue !== null
          ? String(response.referral.projectValue)
          : ''
      );
      setRateDraft(
        String(
          (response.referral.commissionRate ??
            response.referral.effectiveRate ??
            0.1) * 100
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the referral');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action: () => Promise<void>, success?: string) => {
    setError(null);
    setNotice(null);
    try {
      setBusy(true);
      await action();
      if (success) setNotice(success);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const saveStatus = () =>
    run(async () => {
      await updateReferralStatus(id, statusDraft, statusNote.trim() || undefined);
      setStatusNote('');
      setStatusOpen(false);
      await load();
    }, 'Status updated. The partner has been notified.');

  const saveCommission = () =>
    run(async () => {
      const value = Number(valueDraft);
      const percent = Number(rateDraft);

      if (Number.isNaN(value) || value < 0) {
        throw new Error('The project value must be a positive number.');
      }
      if (Number.isNaN(percent) || percent < 0 || percent > 100) {
        throw new Error('The commission rate must be between 0 and 100 percent.');
      }

      await setReferralCommission(id, {
        projectValue: value,
        commissionRate: percent / 100,
        markDue,
      });
      setCommissionOpen(false);
      setMarkDue(false);
      await load();
    }, 'Commission calculated.');

  const savePayment = () =>
    run(async () => {
      if (!commission) return;
      if (!reference.trim()) {
        throw new Error('A payment reference is required.');
      }
      await markCommissionPaid(commission.id, {
        paymentReference: reference.trim(),
        paidDate: paidDate || undefined,
      });
      setPaidOpen(false);
      setReference('');
      await load();
    }, 'Payment recorded. The partner has been notified.');

  const saveNote = () =>
    run(async () => {
      if (!noteDraft.trim()) return;
      await addReferralNote(id, noteDraft.trim());
      setNoteDraft('');
      await load();
    }, 'Internal note added.');

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  if (!referral) {
    return (
      <section className="py-12">
        <div className="mx-auto max-w-3xl px-4">
          <Alert tone="error">{error || 'Referral not found.'}</Alert>
          <Link to="/admin/partners/referrals" className="btn-secondary mt-6 py-2.5 px-5">
            Back to referrals
          </Link>
        </div>
      </section>
    );
  }

  /** What the commission would come to at the current value and rate. */
  const projectedCommission =
    referral.projectValue !== null
      ? referral.projectValue * (referral.commissionRate ?? referral.effectiveRate ?? 0.1)
      : null;

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/admin/partners/referrals"
          className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M15 19l-7-7 7-7" />
          </svg>
          All referrals
        </Link>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}
        {notice && !error && (
          <Alert tone="success" className="mb-6">
            {notice}
          </Alert>
        )}

        <AnimatedSection animation="slide-up">
          <div className="mb-6 rounded-2xl border border-surface-border bg-surface-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <h1 className="font-display text-2xl font-semibold text-white">
                    {referral.businessName}
                  </h1>
                  <StatusBadge
                    label={REFERRAL_STATUS_LABELS[referral.status]}
                    tone={REFERRAL_STATUS_TONES[referral.status]}
                  />
                </div>
                <p className="text-sm text-muted">
                  {[referral.businessType, referral.city, referral.country]
                    .filter(Boolean)
                    .join(' · ') || 'No business details given'}
                </p>
                <p className="mt-1 text-xs text-muted/70">
                  <span className="font-mono">{referral.referralId}</span> · submitted{' '}
                  {formatDate(referral.submittedAt)}
                  {referral.partner && (
                    <>
                      {' '}
                      by{' '}
                      <Link
                        to={`/admin/partners/partners/${referral.partner.id}`}
                        className="text-primary-400 hover:underline"
                      >
                        {referral.partner.fullName} ({referral.partner.partnerId})
                      </Link>
                    </>
                  )}
                </p>
              </div>

              {canManage && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setStatusOpen(true)}
                    className="btn-secondary py-2.5 px-4 text-sm"
                  >
                    Change status
                  </button>
                  <button
                    type="button"
                    onClick={() => setCommissionOpen(true)}
                    className="btn-secondary py-2.5 px-4 text-sm"
                  >
                    Set value &amp; commission
                  </button>
                  {commission && commission.status !== 'paid' && commission.status !== 'cancelled' && (
                    <button
                      type="button"
                      onClick={() => setPaidOpen(true)}
                      className="btn-primary py-2.5 px-4 text-sm"
                    >
                      Mark commission paid
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-muted">
                Pipeline
              </h2>
              <Pipeline
                stages={REFERRAL_PIPELINE}
                labels={REFERRAL_STATUS_LABELS}
                current={referral.status}
                offTrackNote={
                  referral.status === 'lost'
                    ? 'This referral is marked lost or disqualified.'
                    : undefined
                }
              />
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                The opportunity
              </h2>
              <dl className="space-y-4 text-sm">
                <div>
                  <dt className="mb-1 text-muted">Problem they are experiencing</dt>
                  <dd className="whitespace-pre-wrap text-white">{referral.problem}</dd>
                </div>
                {referral.additionalInformation && (
                  <div>
                    <dt className="mb-1 text-muted">Additional information</dt>
                    <dd className="whitespace-pre-wrap text-white">
                      {referral.additionalInformation}
                    </dd>
                  </div>
                )}
                {referral.identifiedHow && (
                  <div>
                    <dt className="mb-1 text-muted">How the partner found it</dt>
                    <dd className="whitespace-pre-wrap text-white">
                      {referral.identifiedHow}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Internal notes
              </h2>
              <p className="mb-4 text-xs text-muted">
                Only Nexasolutra staff see these — they never reach the partner portal.
              </p>

              {canManage && (
                <div className="mb-5">
                  <textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    rows={3}
                    placeholder="What happened on this deal?"
                    className={`${inputClass} resize-y`}
                  />
                  <button
                    type="button"
                    onClick={saveNote}
                    disabled={busy || !noteDraft.trim()}
                    className="btn-primary mt-3 py-2 px-4 text-sm disabled:opacity-60"
                  >
                    Add note
                  </button>
                </div>
              )}

              {referral.internalNotes.length === 0 ? (
                <p className="text-sm text-muted">No internal notes yet.</p>
              ) : (
                <ul className="space-y-3">
                  {[...referral.internalNotes].reverse().map((note) => (
                    <li
                      key={note.id}
                      className="rounded-lg border border-surface-border bg-surface p-4"
                    >
                      <p className="whitespace-pre-wrap text-sm text-white">{note.body}</p>
                      <p className="mt-2 text-xs text-muted">
                        {note.authorName} · {formatDateTime(note.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Activity
              </h2>
              {activities.length === 0 ? (
                <p className="text-sm text-muted">Nothing recorded yet.</p>
              ) : (
                <ul className="space-y-3">
                  {activities.map((activity) => (
                    <li key={activity.id} className="border-l-2 border-surface-border pl-4">
                      <p className="text-sm text-white">{activity.description}</p>
                      <p className="mt-1 text-xs text-muted">
                        {activity.actorName} · {formatDateTime(activity.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Money
              </h2>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-muted">Partner's estimate</dt>
                  <dd className="text-white">{formatMoney(referral.estimatedValue)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted">Project value</dt>
                  <dd className="text-white">{formatMoney(referral.projectValue)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted">Rate</dt>
                  <dd className="text-white">
                    {formatRate(referral.commissionRate ?? referral.effectiveRate ?? null)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3 border-t border-surface-border pt-3">
                  <dt className="text-muted">Commission</dt>
                  <dd className="font-medium text-primary-400">
                    {formatMoney(commission?.commissionAmount ?? projectedCommission)}
                  </dd>
                </div>
              </dl>

              {commission ? (
                <div className="mt-5 space-y-3 border-t border-surface-border pt-5 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted">Commission status</span>
                    <StatusBadge
                      label={COMMISSION_STATUS_LABELS[commission.status]}
                      tone={COMMISSION_STATUS_TONES[commission.status]}
                    />
                  </div>
                  {commission.dueDate && (
                    <div className="flex justify-between gap-3">
                      <span className="text-muted">Due</span>
                      <span className="text-white">{formatDate(commission.dueDate)}</span>
                    </div>
                  )}
                  {commission.paidDate && (
                    <>
                      <div className="flex justify-between gap-3">
                        <span className="text-muted">Paid</span>
                        <span className="text-white">{formatDate(commission.paidDate)}</span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-muted">Reference</span>
                        <span className="break-all text-right text-white">
                          {commission.paymentReference}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-muted">Recorded by</span>
                        <span className="text-white">{commission.paidByName}</span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="mt-5 border-t border-surface-border pt-5 text-xs text-muted">
                  No commission record yet. Set the project value to create one.
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Business contact
              </h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-muted">Contact</dt>
                  <dd className="text-white">{referral.contactName || '—'}</dd>
                </div>
                <div>
                  <dt className="text-muted">Email</dt>
                  <dd className="break-all text-white">
                    {referral.contactEmail ? (
                      <a
                        href={`mailto:${referral.contactEmail}`}
                        className="hover:text-primary-400"
                      >
                        {referral.contactEmail}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted">Phone</dt>
                  <dd className="text-white">{referral.contactPhone || '—'}</dd>
                </div>
                <div>
                  <dt className="text-muted">Website</dt>
                  <dd className="break-all text-white">
                    {referral.website ? (
                      <a
                        href={referral.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary-400"
                      >
                        {referral.website}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Status history
              </h2>
              <ul className="space-y-3 text-sm">
                {[...referral.statusHistory].reverse().map((entry, index) => (
                  <li key={`${entry.status}-${index}`} className="border-l-2 border-surface-border pl-4">
                    <p className="text-white">{entry.label}</p>
                    {entry.note && <p className="mt-1 text-xs text-muted">{entry.note}</p>}
                    <p className="mt-1 text-xs text-muted">
                      {entry.changedByName} · {formatDateTime(entry.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Status */}
      <Modal
        open={statusOpen}
        title="Change referral status"
        description="The partner sees the new stage in their portal, and is emailed at the key ones."
        onClose={() => setStatusOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setStatusOpen(false)}
              className="btn-secondary py-2.5 px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveStatus}
              disabled={busy}
              className="btn-primary py-2.5 px-5 disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Save status'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm text-muted">Status</label>
            <select
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value as ReferralStatus)}
              className={inputClass}
            >
              {REFERRAL_STATUSES.map((value) => (
                <option
                  key={value}
                  value={value}
                  /* Paying is its own action so the reference and payer are
                     always captured with the date. */
                  disabled={value === 'commission_paid'}
                >
                  {REFERRAL_STATUS_LABELS[value]}
                  {value === 'commission_paid' ? ' (use Mark paid)' : ''}
                </option>
              ))}
            </select>
          </div>

          {(statusDraft === 'won' || statusDraft === 'commission_due') &&
            referral.projectValue === null && (
              <Alert tone="warning">
                Set the project value first — the commission is calculated from it.
              </Alert>
            )}

          <div>
            <label className="mb-2 block text-sm text-muted">Note (optional)</label>
            <textarea
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={3}
              className={`${inputClass} resize-y`}
            />
          </div>
        </div>
      </Modal>

      {/* Commission */}
      <Modal
        open={commissionOpen}
        title="Set value & commission"
        onClose={() => setCommissionOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setCommissionOpen(false)}
              className="btn-secondary py-2.5 px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveCommission}
              disabled={busy}
              className="btn-primary py-2.5 px-5 disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Calculate commission'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm text-muted">Project value (₦)</label>
            <input
              type="number"
              min={0}
              value={valueDraft}
              onChange={(e) => setValueDraft(e.target.value)}
              placeholder="5000000"
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-2 block text-sm text-muted">Commission rate (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={rateDraft}
              onChange={(e) => setRateDraft(e.target.value)}
              className={inputClass}
            />
            <p className="mt-2 text-xs text-muted">
              Defaults to this partner's agreed rate. Change it here for a one-off deal.
            </p>
          </div>

          {Number(valueDraft) > 0 && Number(rateDraft) >= 0 && (
            <div className="rounded-lg border border-primary-500/30 bg-primary-500/5 px-4 py-3 text-sm">
              <span className="text-muted">Commission comes to </span>
              <span className="font-medium text-primary-400">
                {formatMoney((Number(valueDraft) * Number(rateDraft)) / 100)}
              </span>
            </div>
          )}

          <label className="flex items-start gap-3 text-sm text-muted">
            <input
              type="checkbox"
              checked={markDue}
              onChange={(e) => setMarkDue(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#16A34A]"
            />
            <span>
              Mark the commission due now, and tell the partner it is owed.
            </span>
          </label>

          {commission?.status === 'paid' && (
            <Alert tone="warning">
              This commission has already been paid. The paid record is kept as it stands
              and will not be recalculated.
            </Alert>
          )}
        </div>
      </Modal>

      {/* Payment */}
      <Modal
        open={paidOpen}
        title="Record payment"
        description="This is the audit record of the payment, so it cannot be edited afterwards."
        onClose={() => setPaidOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setPaidOpen(false)}
              className="btn-secondary py-2.5 px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={savePayment}
              disabled={busy || !reference.trim()}
              className="btn-primary py-2.5 px-5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? 'Recording…' : 'Mark as paid'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {commission && (
            <div className="rounded-lg border border-surface-border bg-surface px-4 py-3 text-sm">
              <span className="text-muted">Paying </span>
              <span className="font-medium text-white">
                {formatMoney(commission.commissionAmount, commission.currency)}
              </span>
              {referral.partner && (
                <span className="text-muted"> to {referral.partner.fullName}</span>
              )}
            </div>
          )}
          <div>
            <label className="mb-2 block text-sm text-muted">Payment reference *</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Bank transfer reference"
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-2 block text-sm text-muted">Paid on</label>
            <input
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default ReferralDetail;
